"use client";

import {Heart} from "lucide-react";
import type {MouseEvent} from "react";

import {useWishlistToggle} from "@/features/wishlist/hooks/useWishlist";
import {cn} from "@/lib/cn";

interface WishlistToggleButtonProps {
  propertyId: string;
  ariaLabel: string;
  className?: string;
  iconClassName?: string;
  /** Rendered next to the icon, e.g. "Save" / "Saved" — omit for icon-only buttons. */
  label?: (wishlisted: boolean) => string;
  /** Seeds the status query so cards already known to be wishlisted (e.g. the /saved page) skip the extra status round trip. */
  initialWishlisted?: boolean;
}

export function WishlistToggleButton({
  propertyId,
  ariaLabel,
  className,
  iconClassName,
  label,
  initialWishlisted
}: WishlistToggleButtonProps) {
  const {isWishlisted, isPending, toggle} = useWishlistToggle(propertyId, {
    ...(initialWishlisted !== undefined ? {initialWishlisted} : {})
  });

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    // Several call sites nest this button inside a <Link> card — never let
    // the click bubble into a navigation.
    event.preventDefault();
    event.stopPropagation();
    toggle();
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPending}
      aria-label={ariaLabel}
      aria-pressed={isWishlisted}
      className={cn(className, isPending && "opacity-60")}
    >
      <Heart className={cn(iconClassName, isWishlisted && "fill-current text-red-500")} aria-hidden />
      {label ? <span>{label(isWishlisted)}</span> : null}
    </button>
  );
}
